import React, { useState, useEffect } from 'react';
import { Search, Filter, Bell, Mail, MessageSquare, CheckCircle, XCircle, Clock } from 'lucide-react';
import { labNotificationService } from '../../services/labNotificationService';
import { LabNotificationLog } from '../../lib/supabase';
import toast from 'react-hot-toast';

const NotificationLog: React.FC = () => {
  const [notifications, setNotifications] = useState<LabNotificationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await labNotificationService.getAll();
      setNotifications(data);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = (notification.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (notification.message || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (notification.recipient_type || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || notification.status === statusFilter;
    const matchesType = typeFilter === 'all' || notification.notification_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'read':
        return 'bg-purple-100 text-purple-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
      case 'read':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return <Bell className="w-5 h-5 text-blue-600" />;
    }
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'sms':
        return <MessageSquare className="w-4 h-4" />;
      case 'in_app':
        return <Bell className="w-4 h-4" />;
      default:
        return <Bell className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'critical_value':
        return 'bg-red-100 text-red-800';
      case 'result_ready':
        return 'bg-green-100 text-green-800';
      case 'tat_breach':
        return 'bg-orange-100 text-orange-800';
      case 'reagent_low':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notification Log</h1>
          <p className="text-gray-600">Track all lab notifications and alerts</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search notifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="sent">Sent</option>
            <option value="delivered">Delivered</option>
            <option value="read">Read</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="critical_value">Critical Value</option>
            <option value="result_ready">Result Ready</option>
            <option value="tat_breach">TAT Breach</option>
            <option value="reagent_low">Reagent Low</option>
            <option value="general">General</option>
          </select>
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {filteredNotifications.map((notification) => (
          <div key={notification.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-3 flex-1">
                {getStatusIcon(notification.status)}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{notification.subject}</h3>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(notification.notification_type)}`}>
                      {notification.notification_type.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">{notification.message}</p>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      {getMethodIcon(notification.notification_method)}
                      <span className="capitalize">{notification.notification_method}</span>
                    </div>
                    <div>
                      <span className="font-medium">To:</span> {notification.recipient_type}
                    </div>
                    {notification.patient && (
                      <div>
                        <span className="font-medium">Patient:</span> {notification.patient.name}
                      </div>
                    )}
                    {notification.doctor && (
                      <div>
                        <span className="font-medium">Doctor:</span> {notification.doctor.name}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(notification.status)}`}>
                  {notification.status}
                </span>
                <span className="text-xs text-gray-500">
                  {formatDateTime(notification.created_at)}
                </span>
              </div>
            </div>

            {/* Timestamps */}
            <div className="border-t border-gray-200 pt-3 mt-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-600">
                {notification.sent_at && (
                  <div>
                    <span className="font-medium">Sent:</span> {formatDateTime(notification.sent_at)}
                  </div>
                )}
                {notification.delivered_at && (
                  <div>
                    <span className="font-medium">Delivered:</span> {formatDateTime(notification.delivered_at)}
                  </div>
                )}
                {notification.read_at && (
                  <div>
                    <span className="font-medium">Read:</span> {formatDateTime(notification.read_at)}
                  </div>
                )}
                {notification.failed_at && (
                  <div className="text-red-600">
                    <span className="font-medium">Failed:</span> {formatDateTime(notification.failed_at)}
                  </div>
                )}
              </div>
              {notification.error_message && (
                <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700">
                  <span className="font-medium">Error:</span> {notification.error_message}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredNotifications.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No notifications found</p>
        </div>
      )}
    </div>
  );
};

export default NotificationLog;
